import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { renderPillar, renderYaoLine, getWuXingColor, colorizeHtml, calculateLiuYao, calculateWangXiang, calculateTransformRelation, calOriatTraState } from "./_helper_liuyao.jsx";
import  {calculateCalendarInfo}  from "./_calendar.jsx";
import apiService from "../../services/api.js";
import { Copy, Check } from "lucide-react";
import html2canvas from "html2canvas"; // Ensure this is installed
import LiuyaoPublish from "./LiuyaoPublish.jsx";

const LINE_NAMES = ["上爻", "五爻", "四爻", "三爻", "二爻", "初爻"];

// --- Helper Functions (Pure) ---

const getLiuShenWithColor = (text) => {
  if (!text && text !== 0) return "";
  return colorizeHtml(String(text), getWuXingColor(String(text).trim()));
};

const getFuShenWithColor = (text) => {
  if (!text && text !== 0) return "";
  const s = String(text).trim();
  if (s.length >= 2) {
    const stem = s.charAt(0);
    const branch = s.slice(1);
    const stemHtml = colorizeHtml(stem, getWuXingColor(stem, "stem"));
    const branchHtml = colorizeHtml(branch, getWuXingColor(branch, "branch"));
    return `${stemHtml}${branchHtml}`;
  }
  return colorizeHtml(s, getWuXingColor(s, "auto"));
};

const getNaJiaWithColor = (text) => {
  if (!text && text !== 0) return "";
  const s = String(text).trim();
  if (s.length >= 2) {
    const stem = s.charAt(0);
    const branch = s.slice(1);
    const stemHtml = colorizeHtml(stem, getWuXingColor(stem, "stem"));
    const branchHtml = colorizeHtml(branch, getWuXingColor(branch, "branch"));
    return `${stemHtml}${branchHtml}`;
  }
  return colorizeHtml(s, getWuXingColor(s, "auto"));
};

// --- Sub-Components ---

const InfoItem = ({ label, value }) => (
  <div className="flex items-start">
    <span className="text-gray-500 w-auto flex-shrink-0 text-left inline-block">{label}：</span>
    <span className="text-gray-900 font-medium break-all">{value}</span>
  </div>
);

const UserInfoSection = ({ info, containerRef }) => {
  const genderMap = { male: "男", female: "女" };
  const genderStr = info.gender ? (genderMap[info.gender] || info.gender) : "";

  const hasName = !!info.name;
  const hasGender = !!genderStr;
  const hasShudi = !!(info.shudi && info.shudi !== "—");

  if (!hasName && !hasGender && !hasShudi) {
    return null;
  }

  return (
    <div ref={containerRef} className="bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-4 sm:p-6">
      <div className="flex flex-nowrap items-center gap-4 text-sm overflow-x-auto">
        {hasName && <InfoItem label="姓名" value={info.name} />}
        {hasGender && <InfoItem label="性别" value={genderStr} />}
        {hasShudi && <InfoItem label="属地" value={info.shudi} />}
      </div>
    </div>
  );
};

const PillarsSection = ({ yearPillar, monthPillar, dayPillar, hourPillar, dayPillarkongwang }) => (
  <div className="mb-6 flex justify-center">
    <div className="w-auto">
      <div className="grid grid-cols-4 gap-4 text-xs text-gray-500 mb-1 text-center">
        <div className="font-semibold">年柱</div>
        <div className="font-semibold">月柱</div>
        <div className="font-semibold">日柱</div>
        <div className="font-semibold">时柱</div>
      </div>

      <div className="grid grid-cols-4 gap-4 text-center">
        <div className="flex justify-center">{renderPillar(yearPillar)}</div>
        <div className="flex justify-center">{renderPillar(monthPillar)}</div>
        <div className="flex justify-center">{renderPillar(dayPillar)}</div>
        <div className="flex justify-center">{renderPillar(hourPillar)}</div>
      </div>

      <div className="grid grid-cols-4 gap-4 text-xs text-gray-500 mt-1">
        <div></div>
        <div></div>
        <div className="text-center"><span className="text-gray-700 font-medium">{dayPillarkongwang}（空）</span></div>
        <div></div>
      </div>
    </div>
  </div>
);

const GuaTable = ({ originalResult, transformedResult, guaTableData, hasFuShen, hasChangedGua }) => {
  const { originalGuaName, originalPalace, originalShiType } = originalResult;
  const { transformedGuaName, transformedPalace, transformedShiType } = transformedResult;

  // Calculate colSpans based on the number of actual <td> elements
  // Original: LiuShen(1) + FuShen(1) + Symbol(1) + NaJia&LiuQin(1) + ShiYing(1)
  const originalColSpan = 1 + 1 + 1 + 1 + 1;
  // Transformed: Symbol(1) + NaJia&LiuQin(1)
  const transformedColSpan = hasChangedGua ? (1 + 1) : 0;
  const spacerColSpan = hasChangedGua ? 1 : 0;

  return (
    <div className="overflow-x-auto w-full">
      <table className="mx-auto text-base border-collapse whitespace-nowrap">
        <thead>
          <tr>
            {/* Original: LiuShen */}
            <th></th>
            {/* 六神与伏神之间的间隔 */}
            <th className="w-3"></th>
            {/* Original: FuShen */}
            <th></th>
            {/* Original: Symbol (Gua Name) */}
            <th className="text-center font-normal text-base p-1">
              {originalGuaName}
            </th>
            {/* Original: NaJia */}
            <th></th>
            {/* Original: ShiYing */}
            <th></th>

            {/* Spacer */}
            {hasChangedGua && <th className="w-3"></th>}

            {/* Transformed: Symbol (Gua Name) */}
            {hasChangedGua && (
              <>
                <th className="text-center font-normal text-base p-1">
                  {transformedGuaName}
                </th>
                {/* Transformed: NaJia */}
                <th></th>
              </>
            )}
          </tr>
          <tr>
            {/* Original: LiuShen */}
            <th></th>
            {/* 六神与伏神之间的间隔 */}
            <th className="w-3"></th>
            {/* Original: FuShen */}
            <th></th>
            {/* Original: Symbol (Palace) */}
            <th className="text-center font-normal p-0">
              <div className="whitespace-nowrap">
                <span style={{ color: getWuXingColor(originalPalace) }}>{originalPalace}宫 </span>
                <span className="text-sm text-gray-500">({originalShiType})</span>
              </div>
            </th>
            {/* Original: NaJia */}
            <th></th>
            {/* Original: ShiYing */}
            <th></th>

            {/* Spacer */}
            {hasChangedGua && <th className="w-3"></th>}

            {/* Transformed: Symbol (Palace) */}
            {hasChangedGua && (
              <>
                <th className="text-center font-normal p-0">
                  <div className="whitespace-nowrap">
                    <span style={{ color: getWuXingColor(transformedPalace) }}>{transformedPalace}宫 </span>
                    <span className="text-sm text-gray-500">({transformedShiType})</span>
                  </div>
                </th>
                {/* Transformed: NaJia */}
                <th></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: LINE_NAMES.length }).map((_, displayIdx) => {
            const sourceIdx = LINE_NAMES.length - 1 - displayIdx;
            const yao = guaTableData[sourceIdx];
            if (!yao) return (
              <tr key={`empty-${displayIdx}`}><td colSpan={originalColSpan + spacerColSpan + transformedColSpan} className="p-1 text-center">—</td></tr>
            );

            const isChanging = yao.isChanging;
            const rowClass = "";

            return (
              <tr key={yao.idx ?? sourceIdx} className={rowClass}>
                {/* LiuShen */}
                <td className="p-0 text-center">
                  <div className="text-gray-500 text-center" dangerouslySetInnerHTML={{ __html: getLiuShenWithColor(yao.liuShen) }} />
                </td>

                {/* 六神与伏神之间的间隔 */}
                <td className="w-3"></td>

                {/* FuShen */}
                <td className="p-0 text-center">
                  <div className="flex items-center justify-center gap-0">
                    <div dangerouslySetInnerHTML={{ __html: getFuShenWithColor(yao.fuShen?.liu_qin) }} />
                    <div dangerouslySetInnerHTML={{ __html: getFuShenWithColor(yao.fuShen?.stem_branch) }} />
                   
                  </div>
                </td>
                {/* Original Gua: NaJia & LiuQin */}
                <td className="p-0 text-center">
                  <div className="flex items-center justify-center gap-0">
                    <div className="font-normal ml-1">{typeof yao.liuqin === "object" ? JSON.stringify(yao.liuqin) : yao.liuqin}</div>
                    <div dangerouslySetInnerHTML={{ __html: getNaJiaWithColor(yao.naJia) }} />

                    {yao.wangXiang && (
                      <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.7em', lineHeight: '1.4' }}>
                        <div>{yao.wangXiang.day}</div>
                        <div>{yao.wangXiang.month}</div>
                      </div>
                    )}
                    <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.7em', lineHeight: '1.4' }}>
                      <div>{yao.transformRelation}</div>
                      <div> {yao.transformState}</div>
                     
                    </div>
                  </div>
                </td>
                {/* Original Gua: Symbol */}
                <td className="p-0 text-center font-bold text-red-600">
                  {renderYaoLine(yao.symbol)}
                </td>
                {/* ShiYing */}
                <td className="p-0 text-center">
                  <div className="p-0 text-center  text-gray-600">{yao.shiYing || ""}</div>
                </td>
               
                {/* Spacer */}
                {hasChangedGua && <td className="w-3"></td>}
               
                {/* Transformed Gua: NaJia & LiuQin */}
                {hasChangedGua && (
                  <>
                    <td className="p-0 text-center align-top">
                      <div className="flex items-center justify-center gap-0">
                        <div className="font-normal ml-1">{typeof yao.transformedliuqin === "object" ? JSON.stringify(yao.transformedliuqin) : yao.transformedliuqin}</div>
                        <div dangerouslySetInnerHTML={{ __html: getNaJiaWithColor(yao.transformedNaJia) }} />
                        {yao.transformedWangXiang && (
                          <div className="flex flex-col justify-center ml-0.5 text-gray-400" style={{ fontSize: '0.7em', lineHeight: '1.4' }}>
                            <div>{yao.transformedWangXiang.day}</div>
                            <div>{yao.transformedWangXiang.month}</div>
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Transformed Gua: Symbol */}
                    <td className="p-0 text-center font-bold text-red-600 align-top">
                      {renderYaoLine(yao.transSymbol)}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AnalysisSection = ({ analysisResult, hasChangedGua }) => (
  <div className="text-sm sm:text-base text-gray-700 space-y-4">
    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100 shadow-sm">
      <h4 className="font-bold text-indigo-800 mb-1">卦意分析</h4>
      <div className="text-gray-600 text-sm space-y-2">
        <div>
          <p className="font-medium text-indigo-700">{analysisResult.original.guaName}</p>
          <p><strong>性质：</strong>{analysisResult.original.nature}</p>
          <p><strong>属性：</strong>{analysisResult.original.attribute}</p>
          <p><strong>卦辞：</strong>{analysisResult.original.gua_ci}</p>
        </div>
        {hasChangedGua && (
          <div className="mt-2 pt-2 border-t border-indigo-100">
            <p className="font-medium text-indigo-700">{analysisResult.transformed.guaName}</p>
            <p><strong>性质：</strong>{analysisResult.transformed.nature}</p>
            <p><strong>属性：</strong>{analysisResult.transformed.attribute}</p>
            <p><strong>卦辞：</strong>{analysisResult.transformed.gua_ci}</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const FeedbackSection = ({ feedback, setFeedback }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">命主反馈</label>
    <textarea
      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
      rows="6"
      value={feedback}
      onChange={(e) => setFeedback(e.target.value)}
      placeholder="在此记录命主的反馈信息..."
    />
  </div>
);

const VerdictSection = ({ analysis, setAnalysis }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">断语 / 分析</label>
    <textarea
      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
      rows="6"
      value={analysis}
      onChange={(e) => setAnalysis(e.target.value)}
      placeholder="在此记录您的断语分析..."
    />
  </div>
);

// --- Main Component ---

const LiuyaoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 1. State Initialization
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [activeTab, setActiveTab] = useState("diagnosis");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);
  
  // Refs for capture
  const userInfoRef = useRef(null);
  const liuyaoRef = useRef(null);

  // Initialize state from location or defaults
  useEffect(() => {
    const locationState = location.state || {};

    if (locationState.liuyaoResult && locationState.requestPayload) {
      setResult(locationState.liuyaoResult);
      setForm(locationState.requestPayload);
      setFeedback(locationState.feedback || locationState.requestPayload.mingzhu_feedback || '');
      setAnalysis(locationState.analysis || locationState.requestPayload.master_record || '');
    } else if (locationState.result && locationState.form) {
      setResult(locationState.result);
      setForm(locationState.form);
      setFeedback(locationState.feedback || '');
      setAnalysis(locationState.analysis || '');
    } else if (id) {
      // Fetch if ID exists and no local state
      setLoading(true);
      const token = localStorage.getItem("access_token");
      axios
        .get(`/api/liuyao/${id}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        .then((res) => {
          const data = res.data?.data || res.data;
          if (data) {
            const fetchedForm = data.requestPayload || {
              mingzhuName: data.mingzhu_name,
              mingzhuGender: data.mingzhu_gender,
              shudi: data.shudi,
              zhanti: data.zhanti,
              zhanti_type: data.zhanti_type,
              datetimeStr: data.qiguatime,
              chart_id: data.id,
              gua_code: data.gua_code,
            };

            let calculatedResult = data.liuyaoResult || data;

            // Recalculate if needed
            if (!data.liuyaoResult && data.gua_code && data.qiguatime) {
              try {
                const guaCodeStr = String(data.gua_code);
                const divinationNumbers = guaCodeStr.split('').map(Number);
                calculatedResult = calculateLiuYao(data.qiguatime, false, divinationNumbers);
                if (data.id) calculatedResult.id = data.id;
              } catch (e) {
                console.error("Calculation failed:", e);
              }
            }

            setResult(calculatedResult);
            setForm(fetchedForm);
            setFeedback(data.mingzhu_feedback || '');
            setAnalysis(data.master_record || '');
          } else {
            setError("未能获取数据");
          }
        })
        .catch((err) => {
          setError(err.response?.data?.error || err.message || "请求失败");
        })
        .finally(() => setLoading(false));
    }
  }, [id, location.state]);

  // 2. Data Processing (Memoized)
  const processedData = useMemo(() => {
    if (!result || !form) return null;

    const userInfo = result?.userInfo || {};
    const dateInfo = result.date_info || result.dateInfo || {};

    // Get raw values with fallback
    const rawYear = form.birth_year || userInfo.birth_year;
    const rawMonth = form.birth_month || userInfo.birth_month;
    const rawDay = form.birth_day || userInfo.birth_day;
    // Handle 0 hour correctly (as 0 is falsy but valid)
    let rawHour = form.birth_hour;
    if (rawHour === undefined || rawHour === null || rawHour === "") rawHour = userInfo.birth_hour;

    // --- Logic: Display String ---
    // If no year, do not display anything (even if month/day exist).
    // If year exists, display as much as available.
    let birthDateStr = null;
    if (rawYear) {
      birthDateStr = `${rawYear}年`;
      if (rawMonth) {
        birthDateStr += `${rawMonth}月`;
        if (rawDay) {
          birthDateStr += `${rawDay}日`;
          if (rawHour !== undefined && rawHour !== null && rawHour !== "") {
            birthDateStr += `${rawHour}时`;
          }
        }
      }
    }

    // --- Logic: Calculate Birth Year Pillar ---
    // Auto-fill defaults if partial data to ensure calculation works
    let birthYearPillar = null;
    if (rawYear) {
      const bYear = rawYear;
      // Defaults: Month=1, Day=1, Hour=0 if missing
      const bMonth = rawMonth || 1;
      const bDay = rawDay || 1; 
      const bHour = (rawHour !== undefined && rawHour !== null && rawHour !== "") ? rawHour : 0;
      
      const pYear = String(bYear).padStart(4, "0");
      const pMonth = String(bMonth).padStart(2, "0");
      const pDay = String(bDay).padStart(2, "0");
      const pHour = String(bHour).padStart(2, "0");
      
      try {
         // Format: YYYYMMDDHHmmss
         const dt = `${pYear}${pMonth}${pDay}${pHour}0000`;
         const cal = calculateCalendarInfo(dt, true);
         if (cal && cal.ganzhi) {
            birthYearPillar = cal.ganzhi.year;
         }
      } catch(e) { 
        console.error("Birth pillar calculation error:", e);
      }
    }
    // ----------------------------------------------

    const rawName = (userInfo.mingzhuName || form.mingzhuName || form.mingzhu_name || "").toString().trim();
    const rawGender = (userInfo.mingzhuGender || form.mingzhuGender || form.mingzhu_gender || "").toString().trim();
    const rawShudi = (userInfo.shudi || form.shudi || "").toString().trim();

    const baseResult = {
      name: rawName || null,
      gender: rawGender || null,
      shudi: userInfo.shudi || form.shudi || "—",
      birthDateStr: birthDateStr,
      zhanlei: userInfo.zhantiType || form.zhanti_type || "—",
      zhanti: userInfo.zhanti || form.zhanti || form.question || "—",
      birthYearPillar, // Add to result
      datetimeDisplayStr: dateInfo.datetimeStrformat || "—",
      yearPillar: dateInfo.yearPillar || "—",
      monthPillar: dateInfo.monthPillar || "—",
      dayPillar: dateInfo.dayPillar || "—",
      dayPillarkongwang: dateInfo.dayPillarkongwang || "—",
      hourPillar: dateInfo.hourPillar || "—"
    };

    baseResult.shudi = rawShudi || "—";

    const original = result.ben_gua || {};
    const changing = result.changing_yao || {};
    const transformed = result.bian_gua || {};
    const divParams = result.divination_params || {};
    const yaoDetails = divParams.yao_details || divParams.yaoDetails || [];

    const originalResult = {

      originalCodeSymbol: original.ben_gua_dongyao || [],
      originalLiuShen: original.liu_shen || [],
      originalFuShen: original.fu_shen || [],
      originalNaJia: original.stems?.map((s, i) => `${s}${original.branches?.[i] ?? ""}`) || (original.na_jia || []),
      originalLiuqin: original.liu_qin || [],
      changingPositions: changing.positions || [],
      shiPos: Number(original.shi_yao) || null,
      originalPalace: original.palace || original.palace_name || "",
      originalShiType: original.shi_type || "",
      originalGuaName: original.name || original.gua_name || "",
      originalMonthGuaShen: original.month_gua_shen || ""
    };



    const transformedResult = {
      transformedCodeArr: transformed.gua_arr || [],
      transformedLiuqin: transformed.liu_qin || [],
      transformedNaJia: transformed.stems?.map((s, i) => `${s}${transformed.branches?.[i] ?? ""}`) || (transformed.na_jia || []),
      transformedPalace: transformed.palace || transformed.palace_name || "",
      transformedShiType: transformed.shi_type || "",
      transformedGuaName: transformed.name || transformed.gua_name || ""
    };

    const hasFuShen = (originalResult.originalFuShen && Object.keys(originalResult.originalFuShen).length > 0);
    const hasChangedGua = originalResult.changingPositions.length > 0;
    const yingPos = originalResult.shiPos ? ((originalResult.shiPos + 3) > 6 ? originalResult.shiPos + 3 - 6 : originalResult.shiPos + 3) : null;

    const originalNaJia_wangshuai = calculateWangXiang(
      baseResult.dayPillar,
      baseResult.monthPillar,
      originalResult.originalNaJia
    );

    const transformedNaJia_wangshuai = calculateWangXiang(
      baseResult.dayPillar,
      baseResult.monthPillar,
      transformedResult.transformedNaJia
    );

    const transformRelations = calculateTransformRelation(
      originalResult.originalNaJia,
      transformedResult.transformedNaJia
    );

    const transformStates = calOriatTraState(
      originalResult.originalNaJia,
      transformedResult.transformedNaJia,
      baseResult.dayPillar
    );

    const guaTableData = Array.from({ length: 6 }).map((_, idx) => {
      const codeSymbol = originalResult.originalCodeSymbol || [];
      const symbol = codeSymbol[idx] != null ? codeSymbol[idx] : "—";
      const liuShen = originalResult.originalLiuShen[idx] || (yaoDetails[idx] && yaoDetails[idx].liu_shen) || "";
      const liuqin = originalResult.originalLiuqin[idx] || "";
      const fuShen = originalResult.originalFuShen[idx] || "";
      const naJia = originalResult.originalNaJia[idx] || (yaoDetails[idx] && yaoDetails[idx].na_jia) || "";
      const wangXiang = originalNaJia_wangshuai ? originalNaJia_wangshuai[idx] : null;
      const transformedWangXiang = transformedNaJia_wangshuai ? transformedNaJia_wangshuai[idx] : null;
      const transformRelation = transformRelations ? transformRelations[idx] : "　";
      const transformState = transformStates ? transformStates[idx] : "";

      let shiYingLabel = "";
      if (originalResult.shiPos && idx === (originalResult.shiPos - 1)) shiYingLabel = "世";
      else if (yingPos && idx === (yingPos - 1)) shiYingLabel = "应";

      const transformedCodeArr = transformedResult.transformedCodeArr || [];
      const transformedSymbol = transformedCodeArr[idx];
      const transformedNaJia = transformedResult.transformedNaJia[idx] || "";
      const transformedliuqin = transformedResult.transformedLiuqin[idx] || "";

      return {
        liuShen,
        fuShen,
        symbol,
        naJia,
        wangXiang,
        transformedWangXiang,
        transformRelation,
        transformState,
        shiYing: shiYingLabel,
        liuqin,
        transformedliuqin,
        transSymbol: transformedSymbol,
        transformedNaJia: transformedNaJia,
        idx,
        isChanging: Array.isArray(originalResult.changingPositions) && originalResult.changingPositions.includes(idx)
      };
    });

    const analysisResult = {
      original: {
        guaName: originalResult.originalGuaName,
        nature: original.nature || "",
        attribute: original.attribute || "",
        gua_ci: original.gua_ci || ""
      },
      transformed: {
        guaName: transformedResult.transformedGuaName,
        nature: transformed.nature || "",
        attribute: transformed.attribute || "",
        gua_ci: transformed.gua_ci || ""
      }
    };

    return {
      baseResult,
      originalResult,
      transformedResult,
      guaTableData,
      hasChangedGua,
      hasFuShen,
      analysisResult
    };
  }, [result, form]);

  // 3. Handlers
  const handleCopy = async () => {
    if (!liuyaoRef.current) return;
    
    // Optimistic UI update
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    try {
      // Create a temporary container for layout - Mobile Width Simulation
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.top = "-9999px";
      container.style.left = "-9999px";
      // Set to flexible mobile width (e.g. 430px for iPhone Pro Max sizing)
      const mobileWidth = 430; 
      container.style.width = `${mobileWidth}px`;
      container.style.backgroundColor = "#ffffff"; 
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.padding = "0"; // Reset padding
      // Inherit font
      container.style.fontFamily = getComputedStyle(document.body).fontFamily;
      
      // Clone nodes - Only Liuyao Panel
      const liuyaoClone = liuyaoRef.current.cloneNode(true);
      
      // Clean up styles for the image to look like a clean mobile view
      liuyaoClone.style.boxShadow = "none";
      liuyaoClone.style.border = "none";
      liuyaoClone.style.margin = "0";
      liuyaoClone.style.width = "100%";
      liuyaoClone.classList.remove("shadow-md", "rounded-xl", "flex-1"); // Remove layout specific classes
      
      // Ensure the table doesn't get cut off if it's slightly wider, though we prefer it fits.
      // We set overflow visible on the clone so html2canvas captures it all even if it exceeds 430px slightly.
      liuyaoClone.style.overflow = "visible";
      const scrollContainers = liuyaoClone.querySelectorAll(".overflow-x-auto");
      scrollContainers.forEach(el => el.style.overflow = "visible");

      // Remove buttons from clone
      const buttons = liuyaoClone.querySelectorAll("button");
      buttons.forEach(btn => btn.style.display = "none");

      container.appendChild(liuyaoClone);
      document.body.appendChild(container);

      // Capture with high scale for sharp text on mobile
      const scale = 3;
      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: scale, 
        backgroundColor: "#ffffff"
      });
      
      document.body.removeChild(container);
      
      // Add footer
      const footerHeight = 60; // Height in pixels (unscaled)
      const scaledFooterHeight = footerHeight * scale; 
      
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + scaledFooterHeight;
      const ctx = finalCanvas.getContext("2d");
      
      // Fill BG
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      
      // Draw content
      ctx.drawImage(canvas, 0, 0);
      
      // Draw Footer Text
      const fontSize = 14 * scale; // 14px equivalent
      ctx.font = `${fontSize}px sans-serif`; 
      ctx.fillStyle = "#9ca3af"; // Tailwind gray-400
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      
      ctx.fillText("圆運命理系统", finalCanvas.width / 2, canvas.height + 20 * scale);
      ctx.fillText("bazi.tryonebest.com", finalCanvas.width / 2, canvas.height + 40 * scale);
      
      // Copy to clipboard
      finalCanvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
              await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          } catch(e) {
              console.error(e);
              // Fallback: Download the image if clipboard copy fails
              const link = document.createElement('a');
              link.href = finalCanvas.toDataURL("image/png");
              link.download = `liuyao_result_${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              alert("自动复制失败（浏览器权限限制），已为您下载生成的图片。");
          }
      }, "image/png");

    } catch (e) {
      console.error(e);
      alert("生成图片失败，请确认已安装 html2canvas");
    }
  };

  const handleSave = async () => {
    if (!apiService.isAuthenticated()) {
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: {
          liuyaoResult: result,
          requestPayload: form,
          feedback,
          analysis
        }
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      alert('请先登录，登录后将自动返回当前页面');
      navigate('/login');
      return;
    }

    const payloadBase = {
      mingzhu_name: form.mingzhuName || form.mingzhu_name || "",
      mingzhu_gender: form.mingzhuGender || form.mingzhu_gender || "",
      shudi: form.shudi || "",
      zhanti: form.zhanti || form.question || "",
      zhanti_type: form.zhanti_type || "",
      qiguatime: form.datetimeStr || null,
      
      // Add birth date fields
      birth_year: form.birth_year || null,
      birth_month: form.birth_month || null,
      birth_day: form.birth_day || null,
      birth_hour: form.birth_hour || null,

      qigua_yearpillar: result?.date_info?.ganzhi?.year || null,
      qigua_monthpillar: result?.date_info?.ganzhi?.month || null,
      qigua_daypillar: result?.date_info?.ganzhi?.day || null,
      qigua_hourpillar: result?.date_info?.ganzhi?.hour || null,
      gua_code: form.divinationNumbers || form.gua_code || null,
      master_record: analysis,
      mingzhu_feedback: feedback,
      is_feedback: !!(feedback && feedback.trim() !== ""),
    };

    const chartId = form.chart_id || result.id || (id ? parseInt(id) : null);
    let requestData = { ...payloadBase };

    if (chartId) {
      requestData.liuyao_chart_id = chartId;
    } else {
      const userStr = localStorage.getItem("user");
      const userObj = userStr ? JSON.parse(userStr) : null;
      if (userObj?.id) requestData.user_id = userObj.id;
    }

    try {
      const res = await apiService.saveLiuyaoChart(requestData);
      if (res.success) {
        alert(chartId ? "更新成功" : "保存成功");
        if (res.id) {
          setForm(prev => ({ ...prev, chart_id: res.id }));
          setResult(prev => ({ ...prev, id: res.id }));
        }
      } else {
        alert("保存失败: " + res.error);
      }
    } catch (e) {
      alert("保存失败: " + e.message);
    }
  };

  const handlePublish = async () => {
    // 先执行保存操作
    if (!apiService.isAuthenticated()) {
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: {
          liuyaoResult: result,
          requestPayload: form,
          feedback,
          analysis
        }
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      alert('请先登录，登录后将自动返回当前页面');
      navigate('/login');
      return;
    }

    const payloadBase = {
      mingzhu_name: form.mingzhuName || form.mingzhu_name || "",
      mingzhu_gender: form.mingzhuGender || form.mingzhu_gender || "",
      shudi: form.shudi || "",
      zhanti: form.zhanti || form.question || "",
      zhanti_type: form.zhanti_type || "",
      qiguatime: form.datetimeStr || null,
      birth_year: form.birth_year || null,
      birth_month: form.birth_month || null,
      birth_day: form.birth_day || null,
      birth_hour: form.birth_hour || null,
      qigua_yearpillar: result?.date_info?.ganzhi?.year || null,
      qigua_monthpillar: result?.date_info?.ganzhi?.month || null,
      qigua_daypillar: result?.date_info?.ganzhi?.day || null,
      qigua_hourpillar: result?.date_info?.ganzhi?.hour || null,
      gua_code: form.divinationNumbers || form.gua_code || null,
      master_record: analysis,
      mingzhu_feedback: feedback,
      is_feedback: !!(feedback && feedback.trim() !== ""),
    };

    const chartId = form.chart_id || result.id || (id ? parseInt(id) : null);
    let requestData = { ...payloadBase };

    if (chartId) {
      requestData.liuyao_chart_id = chartId;
    } else {
      const userStr = localStorage.getItem("user");
      const userObj = userStr ? JSON.parse(userStr) : null;
      if (userObj?.id) requestData.user_id = userObj.id;
    }

    try {
      const res = await apiService.saveLiuyaoChart(requestData);
      if (res.success) {
        // 保存成功后更新 id，再打开发布弹窗
        if (res.id) {
          setForm(prev => ({ ...prev, chart_id: res.id }));
          setResult(prev => ({ ...prev, id: res.id }));
        }
        setShowPublishModal(true);
      } else {
        alert("保存失败: " + res.error);
      }
    } catch (e) {
      alert("保存失败: " + e.message);
    }
  };

  const handlePublishSubmit = async (formData) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post('/api/liuyao/publish', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        
        alert('发布成功！');
        setShowPublishModal(false);
      } else {
        alert('发布失败: ' + (response.data.message || '未知错误'));
      }
    } catch (error) {
      console.error('发布失败:', error);
      alert('发布失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 4. Render
  if (loading) return <div className="p-6 text-center text-gray-600">加载中...</div>;
  if (error) return <div className="p-6 text-center text-red-600">{error}</div>;
  if (!processedData) return <div className="p-6 text-center text-gray-600">暂无数据</div>;

  const { baseResult, originalResult, transformedResult, guaTableData, hasChangedGua, hasFuShen, analysisResult } = processedData;

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-6 lg:px-8 overflow-x-hidden">
      <div className="max-w-5xl w-full max-w-full mx-auto space-y-4 sm:space-y-6 min-w-0">

        {/* Pass ref to UserInfoSection */}
        <UserInfoSection info={baseResult} containerRef={userInfoRef} />

        {/* 卦象展示 panel */}
        <div ref={liuyaoRef} className="bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
            <div className="flex justify-between items-center border-b pb-2 mb-4 gap-2">
               <div>
                 <h3 className="text-lg font-bold text-gray-900">
                   {baseResult.zhanlei} - {truncateText(baseResult.zhanti, 25)}
                   {baseResult.shudi && baseResult.shudi !== "—" && (
                     <span className="text-sm font-normal text-gray-500 ml-2">({baseResult.shudi})</span>
                   )}
                 </h3>
                 <p className="text-xs text-gray-400 mt-1">
                   {baseResult.datetimeDisplayStr}
                 </p>
               </div>
               {(baseResult.birthYearPillar && baseResult.birthYearPillar !== "—") || (originalResult.originalMonthGuaShen && originalResult.originalMonthGuaShen !== "") ? (
                 <div className="flex items-center text-sm">
                   {baseResult.birthYearPillar && baseResult.birthYearPillar !== "—" && (
                     <>
                       <span className="text-gray-500 mr-2">本命:</span>
                       {renderPillar(baseResult.birthYearPillar)}
                     </>
                   )}
                   {originalResult.originalMonthGuaShen && originalResult.originalMonthGuaShen !== "" && (
                     <>
                       <span className={`text-gray-500 mr-2 ${baseResult.birthYearPillar && baseResult.birthYearPillar !== "—" ? "ml-4" : ""}`}>月卦身:</span>
                       <span>{originalResult.originalMonthGuaShen}</span>
                     </>
                   )}
                 </div>
               ) : null}
            </div>

            <PillarsSection
              yearPillar={baseResult.yearPillar}
              monthPillar={baseResult.monthPillar}
              dayPillar={baseResult.dayPillar}
              hourPillar={baseResult.hourPillar}
              dayPillarkongwang={baseResult.dayPillarkongwang}
            />

            <GuaTable
              originalResult={originalResult}
              transformedResult={transformedResult}
              guaTableData={guaTableData}
              hasChangedGua={hasChangedGua}
              hasFuShen={hasFuShen}
            />

            {/* Copy Button */}
            <div className="flex justify-end mt-4 pt-2 border-t border-gray-100">
               <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 rounded-md transition-colors border border-transparent hover:border-indigo-100"
                  title="复制排盘结果为图片"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "已复制" : "复制结果"}
                </button>
            </div>
        </div>

        {/* 诊断与反馈 panel */}
        <div className="bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
          <div className="flex gap-2 border-b border-gray-100 pb-3 mb-4">
            <button
              onClick={() => setActiveTab("diagnosis")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === "diagnosis"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              六爻诊断
            </button>
            <button
              onClick={() => setActiveTab("feedback")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === "feedback"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              命主反馈
            </button>
            <button
              onClick={() => setActiveTab("verdict")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === "verdict"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              断语分析
            </button>
          </div>

          {activeTab === "diagnosis" ? (
            <AnalysisSection
              analysisResult={analysisResult}
              hasChangedGua={hasChangedGua}
            />
          ) : activeTab === "feedback" ? (
            <FeedbackSection
              feedback={feedback}
              setFeedback={setFeedback}
            />
          ) : (
            <VerdictSection
              analysis={analysis}
              setAnalysis={setAnalysis}
            />
          )}
        </div>

        {/* 底部操作按钮 panel */}
        <div className="bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-4 sm:p-6 w-full min-w-0">
          <div className="flex flex-nowrap justify-center gap-2 sm:gap-4">
            <button
              onClick={handleSave}
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-indigo-600 border border-transparent rounded-md text-xs sm:text-sm font-medium text-white hover:bg-indigo-700 shadow-sm min-w-[96px] sm:min-w-[120px]"
            >
              保存
            </button>
            <button
              onClick={handlePublish}
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-green-600 border border-transparent rounded-md text-xs sm:text-sm font-medium text-white hover:bg-green-700 shadow-sm min-w-[96px] sm:min-w-[120px]"
            >
              发布
            </button>
          </div>
        </div>
      </div>

      {/* 发布弹窗 */}
      <LiuyaoPublish
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onSubmit={handlePublishSubmit}
        chartData={{
          id: result?.id || form?.chart_id,
          zhanti: form?.zhanti,
          zhanti_type: form?.zhanti_type,
        }}
      />
    </div>
  );
};

export default LiuyaoDetail;
